import { Component, ChangeDetectionStrategy, input, output, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import {
  securityCheckOutlineIcon,
  gearIcon,
  infoCircleIcon,
  exclamationCircleIcon,
  userIcon,
  clockIcon,
} from '@progress/kendo-svg-icons';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { ProfessionalServicesFormService } from './services/form.service';
import { ProfessionalServicesData } from 'src/app/core/models/customer';

@Component({
  selector: 'app-professional-services',
  standalone: true,
  templateUrl: './professional-services.component.html',
  styleUrls: ['./professional-services.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProfessionalServicesFormService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputsModule,
    DropDownsModule,
    DateInputsModule,
    LabelModule,
    ButtonsModule,
    IconsModule,
    SwitchModule,
  ],
})
export class ProfessionalServicesComponent extends BaseComponent {
  private formService = inject(ProfessionalServicesFormService);

  // Input/Output - simplified
  public initialData = input<ProfessionalServicesData | null>(null); // Optional initial data for editing
  public dataUpdate = output<ProfessionalServicesData>();
  public validityUpdate = output<boolean>();

  // Kendo UI SVG Icons
  public securityCheckIcon: SVGIcon = securityCheckOutlineIcon;
  public gearIcon: SVGIcon = gearIcon;
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public exclamationTriangleIcon: SVGIcon = exclamationCircleIcon;
  public userIcon: SVGIcon = userIcon;
  public shieldIcon: SVGIcon = securityCheckOutlineIcon; // Using securityCheck as alternative
  public bugIcon: SVGIcon = gearIcon; // Using gearIcon as alternative
  public clockIcon: SVGIcon = clockIcon;

  // Form - self-managed
  public professionalServicesForm = this.formService.createProfessionalServicesForm();

  // Service data
  public professionalServiceOptions = this.formService.getServiceOptions();
  public pentestTypeOptions = this.formService.getPentestTypeOptions();
  public assessmentPackageOptions = this.formService.getAssessmentPackageOptions();

  // Signal to track selected services (works with OnPush change detection)
  public selectedServices = signal<string[]>([]);

  // Computed properties
  public isFormValid = computed(() => this.professionalServicesForm.valid);

  // Service-specific getter methods
  public get showPenetrationTestingFields(): boolean {
    return this.selectedServices().includes('penetration-testing');
  }

  public get showAttackSurfaceFields(): boolean {
    return this.selectedServices().includes('attack-surface-assessment');
  }

  public get showBreachAttackFields(): boolean {
    return this.selectedServices().includes('breach-attack-assessment');
  }

  public get showWebappDomains(): boolean {
    const pentestTypes = this.professionalServicesForm.get('pentestTypes')?.value || [];
    return pentestTypes.includes('web-application');
  }

  constructor() {
    super();
    this.initializeForm();
  }

  private initializeForm(): void {
    // Initialize with existing data if available
    const initialData = this.initialData();
    if (initialData) {
      this.professionalServicesForm = this.formService.createProfessionalServicesForm(initialData);
      this.selectedServices.set(initialData.selectedServices || []);
    }

    // Watch for form changes and emit updates
    this.professionalServicesForm.valueChanges.subscribe((value) => {
      // Update selected services signal first
      this.selectedServices.set(value.selectedServices || []);

      const formData = this.formService.getFormData(this.professionalServicesForm);
      this.dataUpdate.emit(formData);

      // Don't emit validity here - it will be emitted after validation is applied
      // The validity will be emitted from toggleService and other validation methods
    });

    // Emit initial validity state
    this.validityUpdate.emit(this.professionalServicesForm.valid);
  }

  public toggleService(serviceValue: string): void {
    this.formService.toggleService(this.professionalServicesForm, serviceValue);

    // Update conditional validation after service toggle
    if (serviceValue === 'penetration-testing') {
      setTimeout(() => {
        this.formService.updatePentestValidation(this.professionalServicesForm);
      }, 0);
    }

    // Emit validity after validation is applied
    setTimeout(() => {
      const isValid = this.professionalServicesForm.valid;
      this.validityUpdate.emit(isValid);
    }, 0);
  }

  public isServiceSelected(serviceValue: string): boolean {
    return this.selectedServices().includes(serviceValue);
  }

  public getServiceIcon(serviceValue: string): SVGIcon {
    switch (serviceValue) {
      case 'penetration-testing':
        return this.bugIcon;
      case 'incident-response':
        return this.securityCheckIcon;
      case 'vciso':
        return this.userIcon;
      case 'attack-surface-assessment':
        return this.shieldIcon;
      case 'breach-attack-assessment':
        return this.gearIcon;
      default:
        return this.gearIcon;
    }
  }

  // Method to handle pentest type changes
  public onPentestTypesChange(): void {
    if (this.selectedServices().includes('penetration-testing')) {
      setTimeout(() => {
        this.formService.updatePentestValidation(this.professionalServicesForm);
        // Emit validity after validation
        const isValid = this.professionalServicesForm.valid;
        this.validityUpdate.emit(isValid);
      }, 0);
    }
  }

  // Method to reapply validation for all selected services
  public reapplyValidation(): void {
    const selectedServices = this.selectedServices();
    if (selectedServices.length > 0) {
      setTimeout(() => {
        this.formService.reapplyValidationForSelectedServices(this.professionalServicesForm);
        // Emit validity after validation
        const isValid = this.professionalServicesForm.valid;
        this.validityUpdate.emit(isValid);
      }, 0);
    }
  }
}
